import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignUp,
  resendSignUpCode,
  signInWithRedirect,
  resetPassword,
  confirmResetPassword,
  updatePassword,
} from 'aws-amplify/auth';

interface User {
  userId: string;
  email: string;
  name?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  accessToken: string | null;
  pendingConfirmEmail: string | null;
  tokenExpiresAt: number | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  confirmAccount: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,
      accessToken: null,
      pendingConfirmEmail: null,
      tokenExpiresAt: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          // Sign out any existing session first
          try {
            await signOut();
          } catch {
            // Ignore sign out errors
          }
          await signIn({ username: email, password });
          await get().checkAuth();
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      loginWithGoogle: async () => {
        set({ isLoading: true, error: null });
        try {
          // Sign out any existing session first
          try {
            await signOut();
          } catch {
            // Ignore sign out errors
          }
          // This will redirect to Google OAuth page
          await signInWithRedirect({ provider: 'Google' });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Google login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      register: async (email: string, password: string, name?: string) => {
        set({ isLoading: true, error: null });
        try {
          await signUp({
            username: email,
            password,
            options: {
              userAttributes: {
                email,
                name: name || '',
              },
            },
          });
          set({ isLoading: false, pendingConfirmEmail: email });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      confirmAccount: async (email: string, code: string) => {
        set({ isLoading: true, error: null });
        try {
          await confirmSignUp({ username: email, confirmationCode: code });
          set({ isLoading: false, pendingConfirmEmail: null });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Verification failed',
            isLoading: false,
          });
          throw error;
        }
      },

      resendCode: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          await resendSignUpCode({ username: email });
          set({ isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to resend code',
            isLoading: false,
          });
          throw error;
        }
      },

      forgotPassword: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          await resetPassword({ username: email });
          set({ isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Forgot password request failed',
            isLoading: false,
          });
          throw error;
        }
      },

      confirmForgotPassword: async (email: string, code: string, newPassword: string) => {
        set({ isLoading: true, error: null });
        try {
          await confirmResetPassword({
            username: email,
            confirmationCode: code,
            newPassword
          });
          set({ isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Reset password failed',
            isLoading: false,
          });
          throw error;
        }
      },

      changePassword: async (oldPassword: string, newPassword: string) => {
        set({ isLoading: true, error: null });
        try {
          await updatePassword({ oldPassword, newPassword });
          set({ isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Change password failed',
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await signOut({ global: true }); // Sign out from all devices
          // Clear persisted state
          localStorage.removeItem('auth-storage');
          set({
            user: null,
            isAuthenticated: false,
            accessToken: null,
            tokenExpiresAt: null,
            isLoading: false,
            isInitialized: false,
          });
        } catch (error) {
          // Even on error, clear local state
          localStorage.removeItem('auth-storage');
          set({
            user: null,
            isAuthenticated: false,
            accessToken: null,
            tokenExpiresAt: null,
            error: error instanceof Error ? error.message : 'Logout failed',
            isLoading: false,
          });
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const currentUser = await getCurrentUser();
          const session = await fetchAuthSession({ forceRefresh: false });

          // Use ID token for API Gateway Cognito authorizer
          const idToken = session.tokens?.idToken?.toString() || null;

          if (!idToken) {
            throw new Error('No valid session');
          }

          // Get token expiration time (exp claim is in seconds since epoch)
          const tokenPayload = session.tokens?.idToken?.payload;
          const expiresAt = tokenPayload?.exp ? (tokenPayload.exp as number) * 1000 : null;

          // Get name from token claims if available, otherwise fallback to username
          const name = (tokenPayload?.name as string) ||
            (tokenPayload?.given_name as string) ||
            currentUser.username;

          set({
            user: {
              userId: currentUser.userId,
              email: currentUser.signInDetails?.loginId || (tokenPayload?.email as string) || '',
              name: name,
            },
            isAuthenticated: true,
            accessToken: idToken,
            tokenExpiresAt: expiresAt,
            isLoading: false,
            isInitialized: true,
            error: null,
          });

          // Enrich user profile from backend API (optional/best effort)
          try {
            const { api } = await import('../services/api');
            const response = await api.getProfile();
            if (response.success && response.data) {
              get().updateUser({
                name: response.data.name || name,
              });
            }
          } catch (profileError) {
            console.warn('Failed to enrich user profile:', profileError);
          }

          // Set up token refresh before expiry (refresh 5 minutes before expiration)
          if (expiresAt) {
            const refreshTime = expiresAt - Date.now() - (5 * 60 * 1000); // 5 minutes before expiry
            if (refreshTime > 0) {
              setTimeout(() => {
                get().refreshToken().catch(console.error);
              }, refreshTime);
            } else {
              // Token expires soon, refresh immediately
              get().refreshToken().catch(console.error);
            }
          }
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            accessToken: null,
            tokenExpiresAt: null,
            isLoading: false,
            isInitialized: true,
          });
        }
      },

      refreshToken: async () => {
        try {
          const session = await fetchAuthSession({ forceRefresh: true });
          const idToken = session.tokens?.idToken?.toString();

          if (!idToken) {
            throw new Error('Failed to refresh token');
          }

          // Get token expiration time
          const tokenPayload = session.tokens?.idToken?.payload;
          const expiresAt = tokenPayload?.exp ? (tokenPayload.exp as number) * 1000 : null;

          set({
            accessToken: idToken,
            tokenExpiresAt: expiresAt,
          });

          console.log('Token refreshed proactively');

          // Schedule next refresh
          if (expiresAt) {
            const refreshTime = expiresAt - Date.now() - (5 * 60 * 1000);
            if (refreshTime > 0) {
              setTimeout(() => {
                get().refreshToken().catch(console.error);
              }, refreshTime);
            }
          }
        } catch (error) {
          console.error('Failed to refresh token:', error);
          // Force re-authentication
          await get().logout();
        }
      },

      updateUser: (updates) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        tokenExpiresAt: state.tokenExpiresAt,
        pendingConfirmEmail: state.pendingConfirmEmail,
      }),
    }
  )
);
